import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import useAxiosFetch from "../common/useAxiosFetch";
import moment from "moment";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import Chart from "../common/Chart";
import AlertMessage from "../common/AlertMessage";
import LoadPage from "../common/LoadPage";
import { categoryIDtoLabel, getCategoryValue, getRiskSeverity, getScoreBorderClass, getRiskColorVar, shouldSuggestAcceptTreatment, canArchiveRisk } from "../common/helpers";
import { API_BASE_URL } from "../common/config";
import authHeader from "../services/auth-header";
import useSoaCheck from "../common/useSoaCheck";
import { MODAL_TYPES } from "../modals/ModalManager";

const calculateRiskLevels = (risks, duration, unit, rangeBounds) => {
  let startDate = moment().subtract(duration, unit).startOf(unit);
  let endDate = moment().startOf(unit);
  let result = {};

  risks.forEach(risk => {
    let lastScore = parseInt(risk["initial-chance"]) * parseInt(risk["initial-impact"]);
    let lastUpdate = moment(risk.date);
    
    // Sort the update-reasons array
    risk["update-reasons"].sort((a, b) => moment(a.date).isBefore(b.date) ? -1 : 1);

    let currentPeriod = moment(startDate);

    while (currentPeriod.isBefore(endDate)) {
      let period = currentPeriod.format(unit === 'months' ? 'MM-YYYY' : 'DD-MM-YYYY');
      result[period] = result[period] || { risks: 0, totalRiskScore: 0, low: 0, medium: 0, high: 0 };

      // Update the lastScore if there's an update for the current period
      for (let update of risk["update-reasons"]) {
        let updateDate = moment(update.date);
        if (updateDate.isAfter(lastUpdate) && updateDate.isSameOrBefore(currentPeriod)) {
          lastUpdate = updateDate;
          lastScore = parseInt(update.chance) * parseInt(update.impact);
        }
      }

      if (lastUpdate.isSameOrBefore(currentPeriod)) {
        result[period].risks++;
        result[period].totalRiskScore += lastScore; 
        result[period][getRiskSeverity(lastScore, rangeBounds)]++;
      }

      currentPeriod = currentPeriod.add(1, unit);
    }
  });

  return result;
};

const calculateRiskHistory = (risks, duration, unit, trends, rangeBounds) => {
  const riskHistory = calculateRiskLevels(risks, duration, unit, rangeBounds);

  let riskHistoryData = [];

  Object.keys(riskHistory).forEach(key => {
    riskHistoryData.push({
      name: key,
      risks: riskHistory[key].risks, 
      totalRiskScore: riskHistory[key].totalRiskScore,
      avgRiskScore: (riskHistory[key].totalRiskScore/(riskHistory[key].risks || 1)).toFixed(2),
      low: riskHistory[key].low,
      medium: riskHistory[key].medium,
      high: riskHistory[key].high
    });
  });

  // Calculate totalRiskScore and avgRiskScore
  let totalRiskScore = risks.reduce((total, risk) => total + (parseInt(risk['actual-impact'])*parseInt(risk['actual-chance'])), 0);
  let avgRiskScore = (totalRiskScore / (risks.length || 1)).toFixed(2);

  // Add current day to riskHistoryData
  riskHistoryData.push({
    name: moment().format(unit === 'months' ? 'MM-YYYY' : 'DD-MM-YYYY'),
    risks: risks.length,
    totalRiskScore,
    avgRiskScore,
    low: trends.low, 
    medium: trends.medium,
    high: trends.high
  });

  // custom code for viewRiskPage: categorize the totalRiskScore into low/medium/high for visualization
  riskHistoryData = riskHistoryData.map((data) => {
    const severity = getRiskSeverity(data.totalRiskScore, rangeBounds);
    return {
      ...data,
      low: severity === 'low' ? data.totalRiskScore : 0,
      medium: severity === 'medium' ? data.totalRiskScore : 0,
      high: severity === 'high' ? data.totalRiskScore : 0,
    };
  });

  return riskHistoryData;
};

const ViewRiskPage = ({ refreshData }) => {
  const { currentUser, currentOrg, alertMessage, showAlertMessage, openModal } = useAppContext();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const riskId = searchParams.get("riskId");


  const { data, loading, refetch } = useAxiosFetch('/api/risks/' + riskId);

  const [editDates, setEditDates] = useState({});
  const [selectedProject, setSelectedProject] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const { data: projects, loading: projectsLoading } = useAxiosFetch(`/api/projects/byOrg/${currentOrg.id}`);
  const { checkSoaAfterMeasureUpdate } = useSoaCheck();

  // Handle URL hash for tab navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['overview', 'trend', 'history', 'measures'];
    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Handle tab changes and update URL
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    navigate(`${location.pathname}${location.search}#${tabKey}`, { replace: true });
  };

  useEffect(() => {
    if (data && data.projectId) {
      setSelectedProject(data.projectId);
    }
  });

  // Re-fetch risk data when a modal updates it (e.g. RiskModal, UpdateRiskScoreModal)
  useEffect(() => {
    if (refreshData) {
      refetch();
    }
  }, [refreshData]);

  if(!riskId) return <LoadPage text="Please provide a riskId" />;


  let riskHistoryData = [],
    riskData = { name: t('current'), high: 0, medium: 0, low: 0 };

  if(data) {
    const daysAgo = moment().diff(data.date, 'days');

    riskData[getRiskSeverity(data['actual-chance']*data['actual-impact'], currentOrg.rangeBounds)]++;

    riskHistoryData = calculateRiskHistory([data], daysAgo, 'days', riskData, currentOrg.rangeBounds);
  }

  const translateReason = (reason) => {
    if(!reason) return "";

    let translatedReason = reason;

    translatedReason = translatedReason.replace("Measure update:", t("measure_update") + ":");
    translatedReason = translatedReason.replace("Chance:", t("chance") + ":");
    translatedReason = translatedReason.replace("Impact:", t("impact") + ":");
    translatedReason = translatedReason.replace("Owner update:", t("owner_update") + ":");
    translatedReason = translatedReason.replace("Risk archived:", t("risk_archived") + ":");
    translatedReason = translatedReason.replace("Risk restored from archive.", t("risk_restored") + ".");

    translatedReason = translatedReason.replace(/(?:[Ff]rom )(.+?)(?: -> )(.+?)(?:\.)/g, (_, fromValue, toValue) => {
      return t("from_to", { from: t(fromValue),  to: t(toValue) });
    });

    return translatedReason;
  };

  // Toggle edit mode for dates
  const toggleEditMode = (index) => {
    setEditDates((prevEditDates) => ({
      ...prevEditDates,
      [index]: !prevEditDates[index],
    }));
  };

  const scoreToColor = (score) => getScoreBorderClass(score, currentOrg.rangeBounds);

  const moveRisk = async (projectId) => {
    const res = await updateDb("/api/risks/move/" + riskId, { projectId: projectId });

    if (res.success) {
      refetch();
      showAlertMessage("success", "Risk moved successfully");
    }
    else {
      showAlertMessage("error", res.msg || res.error || "Error moving risk");
    }
  };

  const saveDate = async (index, newDate) => {
    const res = await updateDb("/api/risks/update-risk-history-date/" + riskId, {
      newDate: newDate,
      index: index
    });

    if (res.success) {
      refetch();
      toggleEditMode(index);
    }
  };

  const handleMeasureClose = async (updateRisk, riskId, measureObj) => {
    // Close the modal using the modal system
    openModal(null);

    if(updateRisk && riskId && measureObj) {
      const res = await updateDb("/api/risks/update-measure/" + riskId, measureObj);

      if (res.success) {
        showAlertMessage('warning', t('measure_update'));
        refetch();

        // Fetch the updated risk object from the API so the UpdateRiskScoreModal gets fresh data
        const updatedRiskRes = await fetch(`${API_BASE_URL}/api/risks/${riskId}`, { headers: authHeader() });
        const updatedRisk = await updatedRiskRes.json();

        // Check if status changed and potentially trigger risk score update
        if (data.measures[measureObj.index].status !== measureObj.status) {
          openModal(MODAL_TYPES.UPDATE_RISK_SCORE, {
            riskObj: updatedRisk,
            updateReason: `- Measure update: ${measureObj.measure} from ${data.measures[measureObj.index].status} -> ${measureObj.status}.\n`
          });
        } else {
          // Only check SoA here if UpdateRiskScoreModal was not opened;
          // otherwise the SoA check runs when that modal closes.
          const affectedCategories = (measureObj.category || []).map(c => getCategoryValue(c));
          if (affectedCategories.length > 0) {
            checkSoaAfterMeasureUpdate(affectedCategories);
          }
        }
      }
    }
  };

  const restoreRisk = async () => {
    const res = await updateDb("/api/risks/restore/" + riskId, {});

    if (res.success) {
      refetch();
      showAlertMessage("success", t("risk_restored"));
    }
    else {
      showAlertMessage("error", res.error);
    }
  };

  if( !loading && !data) {
    return <LoadPage text={t('no_risk_found')}/>;
  }


  if (loading || !data || projectsLoading || !projects) 
    return <LoadPage />;

  const currentScore = data['actual-chance'] * data['actual-impact'];
  const initialScore = data['initial-chance'] * data['initial-impact'];
  const targetScore = data['expected-chance'] * data['expected-impact'];
  const scoreBadgeClass = (score) => `${scoreToColor(score)}`;

  // Legacy risks may lack updatedAt; fall back to the most recent update reason, then creation.
  const latestUpdateReasonDate = (data['update-reasons'] || []).reduce(
    (latest, u) => (u.date && (!latest || moment(u.date).isAfter(latest)) ? u.date : latest),
    null
  );
  const lastUpdated = data.updatedAt || latestUpdateReasonDate || data.createdAt || data.date;
  const lastUpdatedBy = data.updatedAt ? data.updatedByUser : null;
  const isArchived = data.archived === true;

  return (
    <div id="content">
      {/* Toolbar */}
      {!isArchived && (
        <div className="content-header">
          <div className="white-border-btn" onClick={() => openModal(MODAL_TYPES.RISK, { riskObj: data })}><i className="icon-edit" />{t('update_risk')}</div>
          <div className="white-border-btn" onClick={() => openModal(MODAL_TYPES.UPDATE_RISK_SCORE, {riskObj: data})}><i className="icon-score" />{t('update_score')}</div>
          {currentUser.parentEnvironment !== 'knmt' && (<div className="white-border-btn" onClick={() => openModal(MODAL_TYPES.REVIEW_RISK, {riskObj: data})}><i className="icon-risk-review" /> {t('review_risk')}</div>)}
          {canArchiveRisk(currentUser, data) && (
            <div className="white-border-btn" style={{ float: 'left', marginLeft: 0 }} onClick={() => openModal(MODAL_TYPES.ARCHIVE_RISK, { riskObj: data, silent: true })}>
              <i className="icon-archive" /> {t('archive_risk')}
            </div>
          )}
        </div>
      )}

      <div className="clear-both" />

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      {isArchived && (
        <div className="content-bubble" style={{
          float: 'none',
          boxSizing: 'border-box',
          marginTop: 20,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '20px 24px',
          borderLeft: '4px solid var(--text-color)'
        }}>
          <div style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--light-blue-bg-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="icon-archive" style={{ fontSize: 18 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{t('risk_archived_banner_title')}</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>{t('risk_archived_banner_text')}</div>
            <div style={{ fontSize: 13, marginTop: 8, display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 4 }}>
              <span>
                <i className="icon-clock" style={{ opacity: 0.6, marginRight: 6 }} />
                {t('archived_on_by', {
                  date: data.archivedAt ? moment(data.archivedAt).format('DD-MM-YYYY HH:mm') : '-',
                  user: data.archivedByUser || data.archivedBy || '-',
                })}
              </span>
              {data.archiveReason && (
                <span>
                  <i className="icon-comment" style={{ opacity: 0.6, marginRight: 6 }} />
                  <b>{t('archive_reason')}:</b> {data.archiveReason}
                </span>
              )}
            </div>
          </div>
          {canArchiveRisk(currentUser, data) && (
            <div className="white-border-btn" style={{ flexShrink: 0, float: 'none', marginLeft: 0 }} onClick={restoreRisk}>
              <i className="icon-undo" /> {t('restore_risk')}
            </div>
          )}
        </div>
      )}

      {!isArchived && shouldSuggestAcceptTreatment(data) && (
        <div className="content-bubble" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '16px 20px',
          marginBottom: 24,
          borderLeft: '4px solid var(--green-risk-score)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="icon-idea" style={{ fontSize: 20, color: 'var(--green-risk-score)' }} />
            <div>
              <div style={{ fontWeight: 700 }}>{t('treatment_suggestion_title')}</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{t('treatment_suggestion_text')}</div>
            </div>
          </div>
          <div className="white-border-btn" style={{ flexShrink: 0 }} onClick={() => openModal(MODAL_TYPES.UPDATE_RISK_SCORE, { riskObj: data })}>
            <i className="icon-check" /> {t('treatment_suggestion_action')}
          </div>
        </div>
      )}

      {/* Hero Summary - Redesigned with more vertical space */}
      <div className="content-bubble" style={{ padding: '32px 40px', marginBottom: 32 }}>
        {/* Owner and Description Section */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 32, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 4 }}>{t('risk_owner')}</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>{data.owner}</div>
              <div style={{ fontSize: 16, lineHeight: 1.5, color: 'var(--text-color)' }}>
                <b>{t('risk_description')}:</b> {data.description || '-'}
              </div>
            </div>
            <div style={{ textAlign: 'right', minWidth: 160 }}>
              <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 4 }}>{t('updatedAt')}</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{moment(lastUpdated).format('DD-MM-YYYY')}</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                {moment().diff(lastUpdated, 'days')} {t('days_ago')}
                {lastUpdatedBy && <> &middot; {lastUpdatedBy}</>}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 12 }}>
                {t('date')}: {moment(data.date).format('DD-MM-YYYY')}
              </div>
            </div>
          </div>

          {/* Approval Status Section - Integrated conditionally */}
          {currentUser.parentEnvironment !== 'knmt' && (
            <div style={{
              background: data.riskApproved === true 
                ? 'var(--green-risk-score)'
                : data.riskApproved === false
                ? 'var(--red-risk-score)'
                : 'var(--yellow-risk-score)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: 3,
              marginTop: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '50%', 
                  width: 36, 
                  height: 36, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  {data.riskApproved === true && <i className="icon-check-circle" style={{ fontSize: 18 }} />}
                  {data.riskApproved === false && <i className="icon-cross-circle" style={{ fontSize: 18 }} />}
                  {data.riskApproved !== true && data.riskApproved !== false && <i className="icon-clock" style={{ fontSize: 18 }} />}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {data.riskApproved === true && t('approved')}
                    {data.riskApproved === false && t('rejected')}
                    {data.riskApproved !== true && data.riskApproved !== false && t('pending')}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    {t('approval_status')}
                  </div>
                </div>
              </div>
              {data.riskReviewedBy && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {data.riskReviewedByUser || data.riskReviewedBy}
                  </div>
                  {data.riskReviewedAt && (
                    <div style={{ fontSize: 11, opacity: 0.8 }}>
                      {moment(data.riskReviewedAt).format("DD-MM-YYYY HH:mm")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Risk Scores Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 24,
          padding: '24px 0',
          borderTop: '1px solid var(--light-blue-bg-color)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 12 }}>{t('current_score')}</div>
            <div className={scoreBadgeClass(currentScore)} style={{ 
              width: 60, 
              height: 60, 
              lineHeight: '52px', 
              fontSize: 24, 
              margin: '0 auto 12px' 
            }}>
              {currentScore}
            </div>
            <div style={{ fontSize: 14, color: 'var(--gray-text-color)' }}>
              {t('chance')}: {data['actual-chance']} × {t('impact')}: {data['actual-impact']}
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 12 }}>{t('initial_score')}</div>
            <div className={scoreBadgeClass(initialScore)} style={{ 
              width: 60, 
              height: 60, 
              lineHeight: '52px', 
              fontSize: 24, 
              margin: '0 auto 12px' 
            }}>
              {initialScore}
            </div>
            <div style={{ fontSize: 14, color: 'var(--gray-text-color)' }}>
              {t('chance')}: {data['initial-chance']} × {t('impact')}: {data['initial-impact']}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 12 }}>{t('target_score')}</div>
            <div className={scoreBadgeClass(targetScore)} style={{ 
              width: 60, 
              height: 60, 
              lineHeight: '52px', 
              fontSize: 24, 
              margin: '0 auto 12px' 
            }}>
              {targetScore}
            </div>
            <div style={{ fontSize: 14, color: 'var(--gray-text-color)' }}>
              {t('chance')}: {data['expected-chance']} × {t('impact')}: {data['expected-impact']}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 12 }}>{t('progress')}</div>
            <div style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              margin: '0 auto 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 700,
              background: currentScore <= targetScore 
                ? 'var(--green-risk-score)' 
                : 'var(--red-risk-score)',
              color: 'white'
            }}>
              {currentScore <= targetScore ? (
                <i className="icon-check" />
              ) : (
                <span style={{ fontSize: 16 }}>+{currentScore - targetScore}</span>
              )}
            </div>
            <div style={{ fontSize: 14, color: 'var(--gray-text-color)' }}>
              {currentScore <= targetScore ? t('target_achieved') : `${currentScore - targetScore} ${t('points_above_target')}`}
            </div>
          </div>
        </div>
      </div>

      <div style={{clear: 'both'}} />

      {/* Tabs */}
      <div className="tab-container">
        {[
          { key: 'overview', label: 'Overview', icon: 'icon-overview' },
          { key: 'trend', label: 'Trend', icon: 'icon-trend' },
          { key: 'history', label: t('risk_update_history'), icon: 'icon-history' },
          { key: 'measures', label: t('measures'), icon: 'icon-Maatregelen' },
        ].map(tab => (
          <div
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`tab-item ${activeTab === tab.key ? 'active' : ''}`}
          >
            <i className={tab.icon} /> {tab.label}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      <div className="content-bubble" style={{ padding: 40, marginTop: 0 }}>
        <div className="view-risk-content">
          {activeTab === 'overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {/* Left column */}
              <div>
                <div className="risk-domain" style={{ marginBottom: 12 }}>
                  <b>{t('domain')}: </b>
                  {!isEditing ? (
                    <>
                      {projects?.find((project) => project.id === data.projectId)?.name}
                      {!isArchived && (currentUser.role === 'superadmin' || currentUser.role === 'admin') && (
                        <i
                          onClick={() => setIsEditing(true)}
                          className="icon-transfer"
                          style={{ marginLeft: '10px', fontSize: 20, cursor: 'pointer' }}
                        />
                      )}
                    </>
                  ) : (
                    <select
                      value={selectedProject}
                      onChange={(e) => {
                        setSelectedProject(e.target.value);
                        moveRisk(e.target.value);
                        setIsEditing(false);
                      }}
                    >
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="risk-aic" style={{ marginBottom: 12 }}>
                  <b>{t('availability')} / {t('integrity')} / {t('confidentiality')}:</b>
                  <div style={{ marginTop: 6, display: 'flex', gap: 10 }}>
                    <label><input type="checkbox" checked={data.availability} disabled /> {t('availability')}</label>
                    <label><input type="checkbox" checked={data.integrity} disabled /> {t('integrity')}</label>
                    <label><input type="checkbox" checked={data.confidentiality} disabled /> {t('confidentiality')}</label>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <b>{t('risk_treatment')}:</b> {data.treatment ? t(data.treatment) : ''}
                  {(data.treatment === 'avoid' || data.treatment === 'transfer') && (
                    <div><b>{t('how')}?:</b> {data['treatmentHow']}</div>
                  )}
                  {data.treatment === 'accept' && (
                    <div><b>{t('temporarily_accept')}?:</b> {data['tempAcceptTreatment'] ? t('yes') : t('no')}</div>
                  )}
                  {data.treatment === 'accept' && data['tempAcceptTreatment'] && (
                    <div><b>{t('until_when')}:</b> {moment(data['acceptTreatmentDate']).format('DD-MM-YYYY')}</div>
                  )}
                </div>

                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  <div><b>{t('risk')} {t('created_at')}:</b> {data.createdAt && moment(data.createdAt).format('DD-MM-YYYY HH:mm:ss')}</div>
                  <div><b>{t('risk')} {t('created_by')}:</b> {data.createdByUser || data.createdBy}</div>
                  <div><b>{t('risk')} {t('last_updated_at')}:</b> {data.updatedAt && moment(data.updatedAt).format('DD-MM-YYYY HH:mm:ss')}</div>
                  <div><b>{t('risk')} {t('last_updated_by')}:</b> {data.updatedByUser || data.updatedBy}</div>
                </div>
              </div>

              {/* Right column */}
              <div>
                <h4 style={{ marginTop: 0 }}>{t('risk_scores')}</h4>
                <table className="spaced-table">
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>{t('chance')}</th>
                      <th>{t('impact')}</th>
                      <th>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t('initial')}</td>
                      <td>{data['initial-chance']}</td>
                      <td>{data['initial-impact']}</td>
                      <td>
                        <div className={scoreToColor(initialScore)}>
                          {initialScore}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>{t('current')}</td>
                      <td>{data['actual-chance']}</td>
                      <td>{data['actual-impact']}</td>
                      <td>
                        <div className={scoreToColor(currentScore)}>
                          {currentScore}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>{t('target')}</td>
                      <td>{data['expected-chance']}</td>
                      <td>{data['expected-impact']}</td>
                      <td>
                        <div className={scoreToColor(targetScore)}>
                          {targetScore}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'trend' && (
            <div>
              {/* Trend Summary Cards */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: 16, 
                marginBottom: 32 
              }}>
                {/* Risk Score Trend */}
                <div style={{
                  background: 'var(--text-color)',
                  color: 'white',
                  padding: 20,
                  borderRadius: 3,
                  boxShadow: 'var(--btn-box-shadow)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <i className="icon-trend" style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>
                        {currentScore}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                        {t('current_risk_score')}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
                    {currentScore > initialScore ? (
                      <span style={{ color: '#FEF2F2' }}>
                        <i className="icon-arrow-up" /> +{currentScore - initialScore} {t('from_initial')}
                      </span>
                    ) : currentScore < initialScore ? (
                      <span style={{ color: '#F0FDF4' }}>
                        <i className="icon-arrow-down" /> -{initialScore - currentScore} {t('from_initial')}
                      </span>
                    ) : (
                      <span style={{ opacity: 0.7 }}>
                        <i className="icon-minus" /> {t('unchanged_from_initial')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Target Progress */}
                <div style={{
                  background: targetScore < currentScore ? 
                    'var(--red-risk-score)' : 
                    'var(--green-risk-score)',
                  color: 'white',
                  padding: 20,
                  borderRadius: 3,
                  boxShadow: 'var(--btn-box-shadow)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <i className={targetScore < currentScore ? "icon-target" : "icon-check-circle"} style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>
                        {Math.abs(currentScore - targetScore)}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                        {targetScore < currentScore ? t('points_above_target') : t('points_to_target')}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
                    {targetScore < currentScore ? (
                      <>
                        <i className="icon-Risico" /> {t('exceeds_target_risk')}
                      </>
                    ) : (
                      <>
                        <i className="icon-trophy" /> {t('target_achieved')}
                      </>
                    )}
                  </div>
                </div>

                {/* Days Since Creation */}
                <div style={{
                  background: 'var(--yellow-risk-score)',
                  color: 'white',
                  padding: 20,
                  borderRadius: 3,
                  boxShadow: 'var(--btn-box-shadow)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <i className="icon-calendar" style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>
                        {moment().diff(data.date, 'days')}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                        {t('days_since_creation')}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
                    <i className="icon-clock" /> {moment(data.date).format('MMM DD, YYYY')}
                  </div>
                </div>

                {/* Update Frequency */}
                <div style={{
                  background: 'var(--gray-text-color)',
                  color: 'white',
                  padding: 20,
                  borderRadius: 3,
                  boxShadow: 'var(--btn-box-shadow)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <i className="icon-refresh" style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>
                        {data['update-reasons']?.length || 0}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                        {t('total_updates')}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
                    {data['update-reasons']?.length > 0 ? (
                      <>
                        <i className="icon-statistics" /> 
                        {(data['update-reasons'].length / Math.max(moment().diff(data.date, 'days'), 1) * 30).toFixed(1)} {t('updates_per_month')}
                      </>
                    ) : (
                      <>
                        <i className="icon-pause" /> {t('no_updates_yet')}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Chart Section */}
              <div style={{ 
                background: 'white',
                border: '1px solid var(--light-blue-bg-color)',
                borderRadius: 3,
                padding: 24,
                marginBottom: 24,
                boxShadow: 'var(--btn-box-shadow)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 20 
                }}>
                  <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="icon-trend" style={{ color: 'var(--primary-color)' }} />
                    {t('risk_score_evolution')}
                  </h4>
                  <div style={{ fontSize: 12, color: 'var(--gray-text-color)' }}>
                    {moment(data.date).format('MMM DD, YYYY')} - {moment().format('MMM DD, YYYY')}
                  </div>
                </div>
                
                <div style={{ height: 400 }}>
                  <Chart risksThroughTime={riskHistoryData} viewRiskPage="true" chartType="Area" />
                </div>
              </div>

              {/* Risk Score Analysis */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: 24 
              }}>
                {/* Score Breakdown */}
                <div style={{
                  background: 'white',
                  border: '1px solid var(--light-blue-bg-color)',
                  borderRadius: 3,
                  padding: 24,
                  boxShadow: 'var(--btn-box-shadow)'
                }}>
                  <h5 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="icon-score" style={{ color: 'var(--primary-color)', fontSize: 'inherit', marginRight: 0, cursor: 'default' }} />
                    {t('score_breakdown')}
                  </h5>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Current Score Details */}
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                        {t('current')} ({currentScore})
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ 
                          flex: 1,
                          background: 'var(--light-blue-bg-color)',
                          height: 8,
                          borderRadius: 4,
                          position: 'relative'
                        }}>
                          <div style={{
                            background: getRiskColorVar(currentScore, currentOrg.rangeBounds),
                            height: '100%',
                            width: `${Math.min((currentScore / 25) * 100, 100)}%`,
                            borderRadius: 4
                          }} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--gray-text-color)', minWidth: 120 }}>
                          {t('chance')}: {data['actual-chance']} × {t('impact')}: {data['actual-impact']}
                        </div>
                      </div>
                    </div>

                    {/* Target Score Details */}
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                        {t('target')} ({targetScore})
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ 
                          flex: 1,
                          background: 'var(--light-blue-bg-color)',
                          height: 8,
                          borderRadius: 4,
                          position: 'relative'
                        }}>
                          <div style={{
                            background: getRiskColorVar(targetScore, currentOrg.rangeBounds),
                            height: '100%',
                            width: `${Math.min((targetScore / 25) * 100, 100)}%`,
                            borderRadius: 4,
                            opacity: 0.6
                          }} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--gray-text-color)', minWidth: 120 }}>
                          {t('chance')}: {data['expected-chance']} × {t('impact')}: {data['expected-impact']}
                        </div>
                      </div>
                    </div>

                    {/* Progress Indicator */}
                    <div style={{
                      background: currentScore <= targetScore ? 
                        'var(--light-blue-bg-color)' :
                        'var(--light-blue-bg-color)',
                      border: `2px solid ${currentScore <= targetScore ? 'var(--green-risk-score)' : 'var(--red-risk-score)'}`,
                      borderRadius: 3,
                      padding: 12,
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        fontSize: 12, 
                        fontWeight: 600,
                        color: currentScore <= targetScore ? 'var(--green-risk-score)' : 'var(--red-risk-score)'
                      }}>
                        {currentScore <= targetScore ? (
                          <>
                            <i className="icon-check-circle" /> {t('target_achieved')}
                          </>
                        ) : (
                          <>
                            <i className="icon-Risico" /> {currentScore - targetScore} {t('points_above_target')}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div style={{
                  background: 'white',
                  border: '1px solid var(--light-blue-bg-color)',
                  borderRadius: 3,
                  padding: 24,
                  boxShadow: 'var(--btn-box-shadow)'
                }}>
                  <h5 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="icon-clock" style={{ color: 'var(--primary-color)' }} />
                    {t('recent_activity')}
                  </h5>
                  
                  {data['update-reasons'] && data['update-reasons'].length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {[...data['update-reasons']]
                        .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
                        .slice(0, 3)
                        .map((update, index) => {
                          const score = update.chance * update.impact;
                          return (
                            <div key={index} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: 12,
                              background: 'var(--light-blue-bg-color)',
                              borderRadius: 3,
                              border: '1px solid var(--border-color)'
                            }}>
                              <div style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: getRiskColorVar(score, currentOrg.rangeBounds)
                              }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
                                  {translateReason(update.reason).substring(0, 60)}
                                  {translateReason(update.reason).length > 60 && '...'}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--gray-text-color)' }}>
                                  {moment(update.date).fromNow()}
                                </div>
                              </div>
                              <div className={scoreToColor(score)} style={{ fontSize: 12, fontWeight: 600 }}>
                                {score}
                              </div>
                            </div>
                          );
                        })}
                      
                      {data['update-reasons'].length > 3 && (
                        <div style={{ 
                          textAlign: 'center', 
                          fontSize: 12, 
                          color: 'var(--primary-color)',
                          cursor: 'pointer',
                          padding: 8
                        }} onClick={() => handleTabChange('history')}>
                          <i className="icon-add" /> {t('view_all')} {data['update-reasons'].length} {t('updates')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: 40,
                      color: 'var(--gray-text-color)'
                    }}>
                      <i className="icon-clock" style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }} />
                      <div style={{ fontSize: 14 }}>{t('no_updates_yet')}</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>{t('updates_will_appear_here')}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="icon-history" style={{ color: 'var(--primary-color)', fontSize: 18 }} />
                  {t('risk_update_history')}
                </h4>
                <div style={{ fontSize: 12, color: 'var(--gray-text-color)' }}>
                  {data['update-reasons']?.length || 0} {t('updates')}
                </div>
              </div>

              {/* Timeline Container */}
              <div style={{ position: 'relative', paddingLeft: 32 }}>
                {/* Vertical Timeline Line */}
                <div style={{
                  position: 'absolute',
                  left: 16,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'linear-gradient(to bottom, var(--primary-color), var(--light-blue-bg-color))',
                  opacity: 0.3
                }} />

                {data['update-reasons'] && 
                 [...data['update-reasons']]
                   .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
                   .map((update, index) => {
                  const isFirst = index === 0;
                  const isLast = index === data['update-reasons'].length - 1;
                  const score = update.chance * update.impact;
                  
                  // Get sorted array to find the correct previous score
                  const sortedUpdates = [...data['update-reasons']].sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
                  const currentUpdateIndex = sortedUpdates.findIndex(u => u.date === update.date && u.reason === update.reason);
                  
                  const previousScore = currentUpdateIndex < sortedUpdates.length - 1 ? 
                    sortedUpdates[currentUpdateIndex + 1].chance * sortedUpdates[currentUpdateIndex + 1].impact : 
                    data['initial-chance'] * data['initial-impact'];
                  const scoreChange = score - previousScore;
                  
                  return (
                    <div key={index} style={{ 
                      position: 'relative', 
                      marginBottom: isLast ? 0 : 24,
                      background: 'var(--content-bg-color)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 3,
                      padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      marginLeft: 16
                    }}>
                      {/* Timeline Dot */}
                      <div style={{
                        position: 'absolute',
                        left: -28,
                        top: 24,
                        width: 12,
                        height: 12,
                        background: scoreChange > 0 ? '#ef4444' : scoreChange < 0 ? '#22c55e' : 'var(--primary-color)',
                        borderRadius: '50%',
                        border: '3px solid white',
                        boxShadow: '0 0 0 2px var(--border-color)',
                        zIndex: 1
                      }} />

                      {/* Score Change Indicator */}
                      {scoreChange !== 0 && (
                        <div style={{
                          position: 'absolute',
                          left: -44,
                          top: 40,
                          background: scoreChange > 0 ? '#fef2f2' : '#f0fdf4',
                          color: scoreChange > 0 ? '#dc2626' : '#16a34a',
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: `1px solid ${scoreChange > 0 ? '#fecaca' : '#bbf7d0'}`
                        }}>
                          {scoreChange > 0 ? '+' : ''}{scoreChange}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      {/* Date Section */}
                      <div style={{ minWidth: 140 }}>
                        {currentUser.role === 'superadmin' && editDates[sortedUpdates.findIndex(u => u.date === update.date && u.reason === update.reason)] ? (
                          <input
                            type="date"
                            defaultValue={moment(update.date).format('YYYY-MM-DD')}
                            onBlur={(e) => saveDate(sortedUpdates.findIndex(u => u.date === update.date && u.reason === update.reason), e.target.value)}
                            autoFocus
                            style={{
                              border: '1px solid var(--primary-color)',
                              borderRadius: 6,
                              padding: '4px 8px',
                              fontSize: 12
                            }}
                          />
                        ) : (
                          <div>
                            <div style={{
                              background: isFirst ? 'var(--primary-color)' : 'var(--light-blue-bg-color)',
                              color: isFirst ? 'white' : 'var(--text-color)',
                              padding: '6px 12px',
                              borderRadius: 3,
                              fontSize: 12,
                              fontWeight: 600,
                              textAlign: 'center',
                              marginBottom: 4
                            }}>
                              {moment(update.date).format('MMM DD')}
                            </div>
                            <div style={{ 
                              fontSize: 11, 
                              color: 'var(--gray-text-color)', 
                              textAlign: 'center' 
                            }}>
                              {moment(update.date).format('YYYY')}
                            </div>
                            {currentUser.role === 'superadmin' && (
                              <i
                                onClick={() => toggleEditMode(sortedUpdates.findIndex(u => u.date === update.date && u.reason === update.reason))}
                                className="icon-edit"
                                style={{ 
                                  marginTop: 4,
                                  fontSize: 11, 
                                  cursor: 'pointer',
                                  color: 'var(--gray-text-color)',
                                  display: 'block',
                                  textAlign: 'center'
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>                        {/* Content Section */}
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: 14, 
                            lineHeight: 1.5, 
                            color: 'var(--text-color)',
                            marginBottom: 12
                          }}>
                            {translateReason(update.reason).split('\n').map((line, lineIndex) => (
                              line.trim() && (
                                <div key={lineIndex} style={{ marginBottom: 4 }}>
                                  {line.trim()}
                                </div>
                              )
                            ))}
                          </div>
                          
                          {/* Risk Metrics */}
                          <div style={{ 
                            display: 'flex', 
                            gap: 12, 
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 12,
                              color: 'var(--gray-text-color)'
                            }}>
                              <span>{t('chance')}:</span>
                              <span style={{ 
                                background: 'var(--light-blue-bg-color)',
                                padding: '2px 6px',
                                borderRadius: 4,
                                fontWeight: 600
                              }}>
                                {update.chance}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 12,
                              color: 'var(--gray-text-color)'
                            }}>
                              <span>{t('impact')}:</span>
                              <span style={{ 
                                background: 'var(--light-blue-bg-color)',
                                padding: '2px 6px',
                                borderRadius: 4,
                                fontWeight: 600
                              }}>
                                {update.impact}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Score Section */}
                        <div style={{ textAlign: 'center', minWidth: 80 }}>
                          <div style={{ fontSize: 10, color: 'var(--gray-text-color)', marginBottom: 4 }}>
                            {t('risk_score')}
                          </div>
                          <div className={scoreToColor(score)}>
                            {score}
                          </div>
                          {scoreChange !== 0 && (
                            <div style={{
                              fontSize: 10,
                              color: scoreChange > 0 ? '#dc2626' : '#16a34a',
                              marginTop: 4,
                              fontWeight: 600
                            }}>
                              {scoreChange > 0 ? '↗' : '↘'} {Math.abs(scoreChange)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Time Ago */}
                      <div style={{
                        position: 'absolute',
                        top: 8,
                        right: 12,
                        fontSize: 10,
                        color: 'var(--gray-text-color)',
                        opacity: 0.7
                      }}>
                        {moment(update.date).fromNow()}
                      </div>
                    </div>
                  );
                })}

                {/* Initial State Indicator */}
                <div style={{ 
                  position: 'relative', 
                  marginTop: 24,
                  background: 'var(--light-blue-bg-color)',
                  border: '1px dashed var(--gray-text-color)',
                  borderRadius: 3,
                  padding: 20,
                  marginLeft: 16,
                  opacity: 0.8
                }}>
                  {/* Timeline Dot */}
                  <div style={{
                    position: 'absolute',
                    left: -28,
                    top: 24,
                    width: 12,
                    height: 12,
                    background: 'var(--gray-text-color)',
                    borderRadius: '50%',
                    border: '3px solid white',
                    boxShadow: '0 0 0 2px var(--border-color)',
                    zIndex: 1
                  }} />

                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <div style={{ minWidth: 140 }}>
                      <div style={{
                        background: 'var(--gray-text-color)',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: 3,
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: 'center',
                        marginBottom: 4
                      }}>
                        {moment(data.date).format('MMM DD')}
                      </div>
                      <div style={{ 
                        fontSize: 11, 
                        color: 'var(--gray-text-color)', 
                        textAlign: 'center' 
                      }}>
                        {moment(data.date).format('YYYY')}
                      </div>
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: 14, 
                        fontWeight: 600,
                        color: 'var(--text-color)',
                        marginBottom: 8
                      }}>
                        <i className="icon-flag" style={{ marginRight: 8, color: 'var(--gray-text-color)' }} />
                        {t('initial_risk_assessment')}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: 12, 
                        alignItems: 'center',
                        fontSize: 12,
                        color: 'var(--gray-text-color)'
                      }}>
                        <span>{t('chance')}: <strong>{data['initial-chance']}</strong></span>
                        <span>{t('impact')}: <strong>{data['initial-impact']}</strong></span>
                      </div>
                    </div>

                    <div style={{ textAlign: 'center', minWidth: 80 }}>
                      <div style={{ fontSize: 10, color: 'var(--gray-text-color)', marginBottom: 4 }}>
                        {t('initial-score')}
                      </div>
                      <div className={scoreToColor(initialScore)}>
                        {initialScore}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Empty State */}
              {(!data['update-reasons'] || data['update-reasons'].length === 0) && (
                <div style={{
                  textAlign: 'center',
                  padding: 60,
                  color: 'var(--gray-text-color)'
                }}>
                  <i className="icon-history" style={{ fontSize: 48, opacity: 0.3, marginBottom: 16 }} />
                  <div style={{ fontSize: 16, marginBottom: 8 }}>{t('no_updates_yet')}</div>
                  <div style={{ fontSize: 14 }}>{t('risk_updates_will_appear_here')}</div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'measures' && (
            <div>
              <h4 style={{ marginTop: 0 }}>{t('measures')}:</h4>
              <table className="spaced-table">
                <thead>
                  <tr>
                    <th>{t('measure')}</th>
                    <th>{t('category')}</th>
                    <th>Status</th>
                    <th>{t('owner_without_prefix')}</th>
                    <th>Deadline {t('date')}</th>
                    <th>{t('link_to_actions')}</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {data.measures?.map((measure, index) => (
                    <tr key={index}>
                      <td>{measure.measure}</td>
                      <td>
                        {measure.category && measure.category.map((category, index) => (
                          <div key={index}>{categoryIDtoLabel(getCategoryValue(category), i18n.language, currentOrg.securityStandard)}</div>
                        ))}
                      </td>
                      <td>{measure.status ? t(measure.status) : ''}</td>
                      <td>{measure.owner}</td>
                      <td>{measure.deadlineDate ? moment(measure.deadlineDate).format('DD-MM-YYYY') : ''}</td>
                      <td>
                        {measure.action && measure.action.startsWith('http') ? (
                          <a href={measure.action} className="action-link" target="_blank" rel="noopener noreferrer">{t('go_to_action')}</a>
                        ) : (
                          measure.action
                        )}
                      </td>
                      <td>
                        {!isArchived && (<i
                          onClick={() => openModal(MODAL_TYPES.MEASURE, {
                            measureObj: {
                              ...measure,
                              measureIndex: index,
                              riskId: data.id,
                              riskDescription: data.description
                            },
                            handleClose: handleMeasureClose,
                            uniqueOwners: Array.from(new Set([data.owner, ...(data.measures?.map(m => m.owner) || [])])).filter(Boolean).sort().map(owner => ({ owner }))
                          })}
                          className="icon-edit"
                          style={{ cursor: 'pointer', margin: 0 }}
                        />)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewRiskPage;